import { useState, useEffect } from 'react';
import { Copy, Check, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import type { Prospect } from './ProspectCard';

interface EmailTemplate {
  id: string;
  name: string;
  subject: string;
  body: string;
  template_type: string;
}

interface EmailDrafterProps {
  prospect: Prospect;
  templateType: 'outreach' | 'follow_up';
  onClose: () => void;
  onDrafted: () => void;
}

const fillTemplate = (text: string, prospect: Prospect) => {
  return text
    .replace(/\{\{contact_name\}\}/g, prospect.contact_name || 'there')
    .replace(/\{\{location_name\}\}/g, prospect.name);
};

const EmailDrafter = ({ prospect, templateType, onClose, onDrafted }: EmailDrafterProps) => {
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<string>('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const fetch = async () => {
      const { data } = await supabase
        .from('email_templates')
        .select('*')
        .eq('template_type', templateType);
      if (data && data.length > 0) {
        setTemplates(data);
        setSelectedTemplate(data[0].id);
        setSubject(fillTemplate(data[0].subject, prospect));
        setBody(fillTemplate(data[0].body, prospect));
      }
    };
    fetch();
  }, [templateType, prospect]);

  const handleTemplateChange = (id: string) => {
    const t = templates.find((t) => t.id === id);
    if (t) {
      setSelectedTemplate(id);
      setSubject(fillTemplate(t.subject, prospect));
      setBody(fillTemplate(t.body, prospect));
    }
  };

  const handleCopy = async () => {
    const full = `Subject: ${subject}\n\n${body}`;
    await navigator.clipboard.writeText(full);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);

    // Log activity
    await supabase.from('prospect_activities').insert({
      prospect_id: prospect.id,
      type: templateType === 'outreach' ? 'email_drafted' : 'follow_up_drafted',
      content: `Template: ${templates.find(t => t.id === selectedTemplate)?.name}\nSubject: ${subject}`,
    });

    toast.success('Copied to clipboard & logged');
    onDrafted();
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
      <div className="bg-card rounded-xl border border-border shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between p-4 border-b border-border">
          <h3 className="font-semibold text-foreground">
            {templateType === 'outreach' ? 'Draft Outreach Email' : 'Draft Follow-up'}
          </h3>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4 flex-1 overflow-y-auto space-y-4">
          {templates.length > 1 && (
            <div>
              <label className="text-xs font-medium text-muted-foreground mb-1 block">Template</label>
              <select
                value={selectedTemplate}
                onChange={(e) => handleTemplateChange(e.target.value)}
                className="w-full border border-border rounded-lg px-3 py-2 text-sm bg-background text-foreground"
              >
                {templates.map((t) => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
            </div>
          )}

          <div>
            <label className="text-xs font-medium text-muted-foreground mb-1 block">To</label>
            <input
              readOnly
              value={prospect.contact_email || 'No email on file'}
              className="w-full border border-border rounded-lg px-3 py-2 text-sm bg-muted text-foreground"
            />
          </div>

          <div>
            <label className="text-xs font-medium text-muted-foreground mb-1 block">Subject</label>
            <input
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              className="w-full border border-border rounded-lg px-3 py-2 text-sm bg-background text-foreground"
            />
          </div>

          <div>
            <label className="text-xs font-medium text-muted-foreground mb-1 block">Body</label>
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={12}
              className="w-full border border-border rounded-lg px-3 py-2 text-sm bg-background text-foreground resize-y"
            />
          </div>
        </div>

        <div className="p-4 border-t border-border flex justify-end">
          <Button onClick={handleCopy}>
            {copied ? <Check className="w-4 h-4 mr-1" /> : <Copy className="w-4 h-4 mr-1" />}
            {copied ? 'Copied!' : 'Copy to Clipboard'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default EmailDrafter;
